import { DashboardState } from "@kuika/kuika-cl-model";
import { IScreenDesignContextState } from "../../contexts/screen-design-context/screen-design-context";

/**
 * When each region of the designer shell shows its first-load placeholder.
 *
 * The obvious gate — `isProjectLoaded !== true` — is WRONG on its own, and the mistake is
 * invisible until you save. `processWithSpinner` (project-provider) drops that flag for the
 * duration of every explicit save and every screen switch as well as the initial load, so a
 * placeholder keyed on it alone would dissolve a populated Elements grid and a filled
 * property list into shimmer each time the user hits save — indistinguishable from data loss.
 *
 * So each region also asks whether its OWN data is missing. That pairing also settles the
 * screen switch, where the route element is NOT remounted (same <ScreenDesign>, new :sdId):
 * terminateSdStore never runs, so the redux store keeps answering with the outgoing tree for
 * the whole fetch window and setScreenDesign swaps in the incoming one atomically. Every
 * dependency stays non-empty, so moving between two designed screens does not blank the shell.
 *
 * Collected here rather than inlined so the regions cannot drift apart, and so the rule is
 * testable without mounting the shell.
 */

/** What a loading region draws: its placeholder, its content, or its content while it reloads. */
export type LoadingPresentationBase = "skeleton" | "content" | "refreshing";

/**
 * The rule above, for any region: the placeholder only while the region has none of its own
 * data. A region that already shows data keeps showing it while it reloads ("refreshing") —
 * a skeleton over data reads as data loss.
 *
 * Pure, so class components that cannot call a hook share it. `useLoadingPresentation`
 * (shared/hooks) adds the show delay and the minimum visible time on top.
 */
export const resolveLoadingPresentation = (isLoading: boolean, hasData: boolean): LoadingPresentationBase => {
  if (hasData) {
    return isLoading ? "refreshing" : "content";
  }
  return isLoading ? "skeleton" : "content";
};

/** The shell regions load with the project, so their "loading" is `isProjectLoaded !== true`. */
const isShellLoading = (sd: IScreenDesignContextState): boolean => sd.isProjectLoaded !== true;

/** The component catalogue: fetched once per project, so absent only on the first load. */
export const shouldShowCatalogueSkeleton = (sd: IScreenDesignContextState): boolean => {
  const hasCatalogue: boolean = (sd.project?.componentSetObj?.components?.length ?? 0) > 0;
  return resolveLoadingPresentation(isShellLoading(sd), hasCatalogue) === "skeleton";
};

/**
 * The property rows, which need a SELECTION, not merely a tree.
 *
 * They were originally gated on the tree, on the assumption that loadProject selects the root
 * DI as it commits it. Measured in the browser, it does not: the tree lands ~2.5s before the
 * selection does, and for that whole window the panel stood empty — placeholder already gone,
 * rows not yet there. Waiting on the selection closes it.
 *
 * Still paired with isProjectLoaded, and that pairing is what keeps this honest: a user who
 * deliberately deselects everything and saves has an empty selection too, and must NOT get
 * shimmer over a panel that is meant to read as blank.
 */
export const shouldShowPropertiesSkeleton = (sd: IScreenDesignContextState): boolean => {
  const hasSelection: boolean = (sd.selectedKuikaIds?.length ?? 0) > 0;
  return resolveLoadingPresentation(isShellLoading(sd), hasSelection) === "skeleton";
};

/** The screen list, which backs the sub-bar's selector and breadcrumb. */
export const shouldShowScreenListSkeleton = (sd: IScreenDesignContextState): boolean => {
  const hasScreenList: boolean = (sd.screenList?.length ?? 0) > 0;
  return resolveLoadingPresentation(isShellLoading(sd), hasScreenList) === "skeleton";
};

/**
 * The report page itself, while the report the canvas will draw is still on its way.
 *
 * Its own data is the loaded tree, not the project: the rulers and the design area are drawn
 * from the paper size long before the iframe has anything in it, so keying on the project alone
 * would leave a page-shaped hole framed by finished chrome — which is exactly what the
 * full-screen spinner used to sit in.
 */
export const shouldShowReportCanvasSkeleton = (sd: IScreenDesignContextState): boolean => {
  // The same pair the shell renders the report canvas on. NOT the derived landing flag: that one
  // answers "is the loaded screen a report", and it is false for the whole load — the tree is
  // exactly what is being waited for — so keying on it would suppress the placeholder when it is
  // due.
  if (sd.dashboardState !== DashboardState.reportDesigner || sd.isCreateReportDesignPageActive === true) {
    return false;
  }
  if (!isShellLoading(sd)) {
    return false;
  }
  // This region's "own data" is not the tree — the tree lands well before the canvas paints — it
  // is the page being on screen, which only the frame knows. Reading it also settles the save
  // case: a save drops isProjectLoaded too, but the page is already up, so the shell keeps its
  // scrim and the report is never covered by a placeholder that would read as data loss.
  return !isReportCanvasPainted();
};

/**
 * Whether the canvas frame is currently showing a page. The frame is a body-level iframe the
 * canvas positions by hand, and it carries its own visibility flag.
 */
const isReportCanvasPainted = (): boolean => {
  const canvasIFrame: HTMLElement | null = document.getElementById("kuika_iframeAgent_canvas");
  return canvasIFrame?.dataset.visible === "true";
};
